/**
 * Display helpers for the day-close hero figures. Consolidates the old-API
 * fallbacks in one place: during a deploy window where the admin is newer
 * than the API, served payloads may lack `waterfall.nonMonetaryTotal` (their
 * netSales then still includes comps) and `collections.collectedTotal` —
 * both are derived locally here, exactly like the API's read-time
 * normalization does. Pure TS, no Vue imports.
 */
import { DayClosePaymentBucket, IDayClose } from '@/interfaces/IDayClose';
import { roundPrice } from '@/utils';

/**
 * Buckets that represent money physically received (mirrors the API's
 * MONEY_CAJA_BUCKETS). Old movements-mode snapshots may carry non-monetary
 * rows (comps / club balance / customer account — every cajeable payment
 * recorded a movement); those are not money and must never enter Cobrado.
 */
const MONEY_BUCKETS: readonly DayClosePaymentBucket[] = [
  'cash',
  'card',
  'online',
  'banktransfer',
  'qr',
];

export interface IDayCloseNetDisplay {
  /** Comps (invitación + consumo empleado) deducted from the net figure. */
  nonMonetary: number;
  /** The hero "Ventas netas" figure (comps always deducted). */
  netSales: number;
}

export function getDayCloseNetDisplay(payload: IDayClose): IDayCloseNetDisplay {
  const wf = payload.waterfall;
  // Defensive reads: very old payloads may lack tenders.comps entirely.
  const comps = roundPrice(
    (payload.tenders?.comps?.invitation?.amount ?? 0) +
      (payload.tenders?.comps?.employee?.amount ?? 0),
  );
  const nonMonetary = wf.nonMonetaryTotal ?? comps;
  // When the API didn't serve nonMonetaryTotal, its netSales still includes
  // comps — deduct locally so the hero always shows true net.
  const netSales =
    wf.nonMonetaryTotal === undefined
      ? roundPrice(wf.netSales - nonMonetary)
      : wf.netSales;
  return { nonMonetary, netSales };
}

export interface IDayCloseDeltaBasis {
  current: number;
  previous: number;
}

/**
 * Like-for-like basis for the hero delta chip: net vs net when the prev
 * snapshot exposes a positive netSales; else dayTotal vs dayTotal (older
 * stored comparisons, or a fully-comped prev day); else null (no chip).
 */
export function getDayCloseDeltaBasis(
  payload: IDayClose,
  displayedNetSales: number,
): IDayCloseDeltaBasis | null {
  const prev = payload.comparisons?.prevSameWeekday;
  if (!prev) return null;
  if (typeof prev.netSales === 'number' && prev.netSales > 0) {
    return { current: displayedNetSales, previous: prev.netSales };
  }
  if (prev.dayTotal > 0) {
    return { current: payload.waterfall.dayTotal, previous: prev.dayTotal };
  }
  return null;
}

export interface IDayCloseCollectedDisplay {
  /** Σ rows.inflows — the "Recibiste" trail figure. */
  received: number;
  refunds: number;
  /** The hero "Cobrado" figure: received − refunds. */
  collectedTotal: number;
}

export function getDayCloseCollectedDisplay(
  payload: IDayClose,
): IDayCloseCollectedDisplay {
  const collections = payload.collections;
  const collectedTotal =
    collections?.collectedTotal ??
    roundPrice(
      (collections?.rows ?? [])
        .filter((row) => MONEY_BUCKETS.includes(row.bucket))
        .reduce((sum, row) => sum + row.inflows - row.outflows, 0),
    );
  const refunds = collections?.refunds ?? 0;
  // Σ rows.outflows === refunds exactly (manual movements never touch rows).
  return {
    received: roundPrice(collectedTotal + refunds),
    refunds,
    collectedTotal,
  };
}

export interface IDayCloseCollectedDelta {
  up: boolean;
  pct: number;
}

/**
 * Pct for the Cobrado legend. Requires prev.collectedTotal > 0 — this metric
 * has NO fallback basis (never borrow dayTotal); null when absent or when
 * the change rounds to 0%.
 */
export function getDayCloseCollectedDelta(
  payload: IDayClose,
  displayedCollected: number,
): IDayCloseCollectedDelta | null {
  const prev = payload.comparisons?.prevSameWeekday;
  if (!prev || typeof prev.collectedTotal !== 'number') return null;
  if (prev.collectedTotal <= 0) return null;
  const pct = Math.round(
    ((displayedCollected - prev.collectedTotal) / prev.collectedTotal) * 100,
  );
  if (pct === 0) return null;
  return { up: pct > 0, pct: Math.abs(pct) };
}
